import moment from 'moment';
import _ from 'lodash';
import formatCurrency from 'format-currency';
import XLSX from 'xlsx';

const REPORT_SUBTITLE = 'Schedule K- Summary of Hours and Amounts on T&M/Labor Hour Contracts';

/** Placeholder other-costs rows aligned with labor rows until API support is added. */
const STATIC_OTHER_COST_ROWS = [
  { otherCost: 'Travel', otherGaRate: '', otherAmount: '$0.00' },
  { otherCost: 'ODC', otherGaRate: '', otherAmount: '$0.00' },
  { otherCost: 'Subk/Conslt.', otherGaRate: '', otherAmount: '$0.00' },
  { otherCost: '', otherGaRate: '', otherAmount: '$0.00' },
  { otherCost: 'Sub-Total', otherGaRate: '', otherAmount: '$0.00' },
  { otherCost: '', otherGaRate: '', otherAmount: '' },
  { otherCost: 'G&A @:', otherGaRate: '$0.00', otherAmount: '$0.00' },
  { otherCost: '', otherGaRate: '', otherAmount: '' },
  { otherCost: '   Subtotal', otherGaRate: '', otherAmount: '$0.00' },
  { otherCost: '', otherGaRate: '', otherAmount: '' },
  { otherCost: 'TOTAL', otherGaRate: '', otherAmount: '$0.00', otherAmountBold: true }
];

const BLANK_OTHER_COST = { otherCost: '', otherGaRate: '', otherAmount: '' };

class ScheduleKSummaryReportController {
  constructor($rootScope, $http, toastr) {
    'ngInject';
    const self = this;
    self.$rootScope = $rootScope;
    self.$http = $http;
    self.toastr = toastr;
    self.formatCurrency = formatCurrency;
    self.reportSubtitle = REPORT_SUBTITLE;
    self.queryResult = [];
    self.contractSections = [];
    self.customerFilter = 'all';
    self.contractFilter = 'all';
    self.contractOptions = [];
    self.loading = false;
    self.searched = false;
    self.exporting = false;

    self.$http.get($rootScope.apiHost + '/api/reports/scheduleKSummaryReport', { withCredentials: true }).then(function (success) {
      self.report = success.data;
      self.queryParams = success.data.filterParameters;
      self.queryParams.dateRange.value = 'thisYear';
      self.search();
    });

    self.$http.get($rootScope.apiHost + '/api/admin/customers', { withCredentials: true }).then(function (success) {
      self.customers = success.data;
    });

    self.opened = {};
    self.open = function ($event, elementOpened) {
      $event.preventDefault();
      $event.stopPropagation();
      self.opened[elementOpened] = !self.opened[elementOpened];
    };

    self.datePickerFormat = 'MMM dd, yyyy';
    self.datePickerOptions = {
      formatYear: 'yyyy',
      formatMonth: 'MMM',
      formatDay: 'dd',
      minDate: new Date(1999, 1, 1),
      startingDay: 1
    };
  }

  search() {
    const self = this;
    if (!self.queryParams) {
      return;
    }

    self.loading = true;
    self.queryResult = [];
    self.contractSections = [];

    if (self.queryParams.dateRange.value === 'custom') {
      if (self.queryParams.startDate.value === 'default') {
        self.queryParams.startDate.value = moment().startOf('month').toDate();
      }
      if (self.queryParams.endDate.value === 'default') {
        self.queryParams.endDate.value = moment().endOf('month').toDate();
      }

      self.queryParams.startDate.value.setHours(0, 0, 0, 0);
      self.queryParams.endDate.value.setHours(0, 0, 0, 0);
    }

    if (self.customerFilter && self.customerFilter !== 'all') {
      self.queryParams.customer.value = self.customerFilter.id;
    } else {
      self.queryParams.customer.value = 'default';
    }

    if (self.contractFilter && self.contractFilter !== 'all') {
      self.queryParams.contract.value = self.contractFilter.id;
    } else {
      self.queryParams.contract.value = 'default';
    }

    if (self.queryParams.taskOrder) {
      self.queryParams.taskOrder.value = 'default';
    }

    self.$http.post(
      self.$rootScope.apiHost + '/api/reports/scheduleKSummaryReport',
      self.queryParams,
      { withCredentials: true }
    ).then(function (success) {
      self.queryResult = success.data || [];
      self.buildContractSections();
      self.searched = true;
      self.loading = false;
    }, function () {
      self.queryResult = [];
      self.contractSections = [];
      self.searched = true;
      self.loading = false;
    });
  }

  selectCustomer() {
    const self = this;
    self.queryParams.contract.value = 'default';
    if (self.queryParams.taskOrder) {
      self.queryParams.taskOrder.value = 'default';
    }
    self.contractFilter = 'all';
    self.contractOptions = [];

    if (self.customerFilter && self.customerFilter !== 'all') {
      self.queryParams.customer.value = self.customerFilter.id;
      self.$http.get(
        self.$rootScope.apiHost + '/api/admin/contracts/bycustomer?customer=' + self.customerFilter.id,
        { withCredentials: true }
      ).then(function (success) {
        self.contractOptions = success.data;
      });
    } else {
      self.queryParams.customer.value = 'default';
    }

    self.search();
  }

  selectContract() {
    const self = this;
    self.queryParams.contract.value = 'default';
    if (self.queryParams.taskOrder) {
      self.queryParams.taskOrder.value = 'default';
    }

    if (self.contractFilter && self.contractFilter !== 'all') {
      self.queryParams.contract.value = self.contractFilter.id;
    }

    self.search();
  }

  buildContractSections() {
    const self = this;
    const sorted = _.sortBy(self.queryResult, ['contract', 'employee', 'laborCategory', 'rate']);
    const grouped = _.groupBy(sorted, 'contractId');

    self.contractSections = _.map(grouped, function (records) {
      const first = records[0];
      let hours = 0;
      let amount = 0;

      records.forEach(function (record) {
        hours += record.hours || 0;
        amount += record.amount || 0;
      });

      return {
        contractNumber: first.contractNumber,
        contractName: first.contract,
        rows: records,
        displayRows: self.buildSectionDisplayRows(records),
        laborSubtotalHours: Math.round(hours * 100) / 100,
        laborSubtotalAmount: Math.round(amount * 100) / 100
      };
    });

    self.contractSections = _.sortBy(self.contractSections, 'contractName');
  }

  buildSectionDisplayRows(laborRows) {
    const rowCount = Math.max(laborRows.length, STATIC_OTHER_COST_ROWS.length);
    const displayRows = [];

    for (let i = 0; i < rowCount; i++) {
      const labor = laborRows[i] || null;
      const other = STATIC_OTHER_COST_ROWS[i] || BLANK_OTHER_COST;

      displayRows.push({
        laborCategory: labor ? labor.laborCategory : '',
        employee: labor ? labor.employee : '',
        rate: labor ? labor.rate : null,
        hours: labor ? labor.hours : null,
        amount: labor ? labor.amount : null,
        otherCost: other.otherCost,
        otherGaRate: other.otherGaRate,
        otherAmount: other.otherAmount,
        otherAmountBold: !!other.otherAmountBold
      });
    }

    return displayRows;
  }

  getCompanyHeader() {
    const customers = _.uniq(this.queryResult.map(function (row) {
      return row.customer;
    }).filter(Boolean));

    return customers.length === 1 ? customers[0] : '';
  }

  getPeriodEndedLabel() {
    const self = this;
    let end;

    if (self.queryParams && self.queryParams.dateRange.value === 'custom' &&
        self.queryParams.endDate.value && self.queryParams.endDate.value !== 'default') {
      end = moment(self.queryParams.endDate.value);
    } else if (self.queryResult.length) {
      const periodEnds = self.queryResult.map(function (row) {
        return row.periodEnd;
      }).filter(Boolean);
      end = moment(_.max(periodEnds));
    } else {
      end = moment();
    }

    return 'For The Year Ended ' + end.format('MM/DD/YY');
  }

  formatHours(value) {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return formatCurrency(Math.round(Number(value) * 100) / 100);
  }

  formatMoney(value) {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return formatCurrency(Number(value));
  }

  formatRate(value) {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return '$' + formatCurrency(Number(value));
  }

  getTotalHours() {
    let total = 0;
    if (this.queryResult) {
      this.queryResult.forEach(function (record) {
        total += record.hours || 0;
      });
    }
    return Math.round(total * 100) / 100;
  }

  getTotalAmount() {
    let total = 0;
    if (this.queryResult) {
      this.queryResult.forEach(function (record) {
        total += record.amount || 0;
      });
    }
    return Math.round(total * 100) / 100;
  }

  buildExportRows() {
    const self = this;
    const rows = [];
    const blankRow = ['', '', '', '', '', '', '', '', ''];

    const company = self.getCompanyHeader();
    if (company) {
      rows.push([company, '', '', '', '', '', '', '', '']);
    }
    rows.push([self.reportSubtitle, '', '', '', '', '', '', '', '']);
    rows.push([self.getPeriodEndedLabel(), '', '', '', '', '', '', '', '']);
    rows.push(blankRow.slice());
    rows.push(['** No G&A applied to travel on T&M contracts', '', '', '', '', '', '', '', '']);
    rows.push(blankRow.slice());

    self.contractSections.forEach(function (section, index) {
      if (index > 0) {
        rows.push(blankRow.slice());
      }
      rows.push(['PRIME CONTRACT NO.:', '', section.contractNumber || '', '', '', '', '', '', '']);
      rows.push(['CONTRACT NAME:', '', section.contractName || '', '', '', '', '', '', '']);
      rows.push(blankRow.slice());
      rows.push(['LABOR', 'Employee', 'RATE', 'HRS', 'AMOUNT', '', 'OTHER COSTS', '', 'AMOUNT']);

      section.displayRows.forEach(function (row) {
        rows.push([
          row.laborCategory || '',
          row.employee || '',
          row.rate != null ? Number(row.rate) : '',
          row.hours != null ? Number(row.hours) : '',
          row.amount != null ? Number(row.amount) : '',
          '',
          row.otherCost || '',
          row.otherGaRate || '',
          row.otherAmount || ''
        ]);
      });

      rows.push([
        'Subtotal - Labor',
        '',
        '',
        section.laborSubtotalHours != null ? Number(section.laborSubtotalHours) : '',
        section.laborSubtotalAmount != null ? Number(section.laborSubtotalAmount) : '',
        '',
        '',
        '',
        ''
      ]);
    });

    return rows;
  }

  exportToSpreadsheet(format) {
    const self = this;
    if (!self.queryResult || !self.queryResult.length) {
      return;
    }

    self.exporting = true;

    try {
      const rows = self.buildExportRows();
      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      worksheet['!cols'] = [
        { wch: 32 },
        { wch: 22 },
        { wch: 14 },
        { wch: 12 },
        { wch: 16 },
        { wch: 8 },
        { wch: 16 },
        { wch: 12 },
        { wch: 16 }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Schedule K Summary');

      const filename = 'schedule-k-summary-report_' + moment().format('MMDDYYYY') + '.' + format;

      if (format === 'csv') {
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        XLSX.writeFile(workbook, filename);
      }

      self.toastr.success('Data exported successfully', 'Export Complete');
    } catch (error) {
      self.toastr.error('Failed to export data', 'Export Error');
    } finally {
      self.exporting = false;
    }
  }

  exportToExcel() {
    this.exportToSpreadsheet('xlsx');
  }

  exportToCsv() {
    this.exportToSpreadsheet('csv');
  }
}

export default ScheduleKSummaryReportController;



// WEBPACK FOOTER //
// ./client/pages/admin/reports/canned-reports/schedule-k-summary-report/schedule-k-summary-report.controller.js